import classnames from 'classnames';
import type { MouseEvent } from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { keyframes } from 'styled-components';

import type { Props as ButtonProps } from '#src/atoms/buttons/Button/index.tsx';
import { Button } from '#src/atoms/buttons/Button/index.tsx';
import { DotsLoader } from '#src/atoms/loader/DotsLoader/index.tsx';
import { BaseLoadingIndicator } from '#src/atoms/loader/LoadingIndicator/index.tsx';
import { useLoadingForMethod } from '#src/hooks/useLoadingForMethod.ts';
import { glossary } from '#src/lib/glossary.ts';

export interface Props extends Omit<ButtonProps, 'onClick'> {
  loading?: boolean;
  dots?: boolean;
  onClick?: (
    e: MouseEvent<HTMLButtonElement>
  ) => void | Promise<void> | Promise<unknown>;
}

const Loading = styled.span`
  margin-left: 10px;
`;
const LoadingIndicator = styled.div`
  font-size: calc(3 * var(--unit));
  &.small {
    font-size: calc(2 * var(--unit));
  }
  &.compact {
    font-size: var(--unit);
  }
`;
const StyledButton = styled(Button)`
  position: relative;
`;

const zoomIn = keyframes`
  to {
    transform: scale(1)
  }
`;

const Loader = styled.span`
  position: absolute;
  display: flex;
  margin: auto;
  transform: scale(0);
  ${StyledButton}.loading & {
    animation: 0.2s ease-in-out ${zoomIn} both;
  }
`;
const Cta = styled.span`
  transition: transform ease-in-out 0.2s;
  transform: scale(1);
  ${StyledButton}.loading & {
    transform: scale(0);
  }
`;

export const LoadingButton = ({
  loading: externalLoading,
  children,
  disabled,
  dots,
  className,
  size: variant,
  onClick,
  ...rest
}: Props) => {
  const [internalLoading, wrappedMethod] = useLoadingForMethod(onClick);
  const loading = externalLoading || internalLoading;

  // Wrap the handler to not return the Promise, preventing Button from
  // detecting it as async and showing its own loading indicator
  const handleClick = wrappedMethod
    ? (e: MouseEvent<HTMLButtonElement>) => {
        wrappedMethod(e);
      }
    : undefined;

  const renderLoader = () => {
    if (dots) {
      return (
        <>
          <FormattedMessage {...glossary.loading} />
          <Loading className={classnames(rest.color)}>
            <DotsLoader />
          </Loading>
        </>
      );
    }
    return (
      <LoadingIndicator className={variant}>
        <BaseLoadingIndicator />
      </LoadingIndicator>
    );
  };

  return (
    <StyledButton
      disabled={disabled || loading}
      className={classnames(className, { loading })}
      size={variant}
      onClick={handleClick}
      {...rest}
    >
      {!!loading && <Loader>{renderLoader()}</Loader>}
      <Cta>{children}</Cta>
    </StyledButton>
  );
};
